import { useEffect, useMemo, useRef, useState } from "react";

const CHORD_TOKEN_PATTERN = /[A-G][#b]?(?:maj|min|m|sus|dim|aug|add|mmaj)?(?:\d+)?(?:\/[A-G][#b]?)?(?:\([^)]*\))?/g;

const isSectionHeader = (line) => /^\[[^\]]+\]$/.test(line.trim());

const isChordLine = (line) => {
  const trimmedLine = line.trim();
  if (!trimmedLine || isSectionHeader(trimmedLine)) {
    return false;
  }

  const withoutChords = trimmedLine.replace(CHORD_TOKEN_PATTERN, "").replace(/\s+/g, "");
  return withoutChords.length === 0;
};

const getChordPositions = (line) => {
  const positions = [];
  const matches = line.matchAll(CHORD_TOKEN_PATTERN);

  for (const match of matches) {
    positions.push({
      chord: match[0],
      index: match.index || 0,
    });
  }

  return positions;
};

const measureCharacterWidth = (fontSize) => {
  if (typeof document === "undefined") {
    return fontSize * 0.62;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = `${fontSize}px Courier New`;
  return context.measureText("M").width || fontSize * 0.62;
};

const wrapLyricLine = (line, maxColumns) => {
  if (!line.trim()) {
    return [];
  }

  const segments = [];
  let start = 0;

  while (start < line.length) {
    const remaining = line.length - start;
    if (remaining <= maxColumns) {
      segments.push({
        start,
        text: line.slice(start),
      });
      break;
    }

    let breakIndex = start + maxColumns;
    while (breakIndex > start && line[breakIndex] !== " ") {
      breakIndex -= 1;
    }

    if (breakIndex === start) {
      breakIndex = start + maxColumns;
    }

    const segmentText = line.slice(start, breakIndex).trimEnd();
    segments.push({
      start,
      text: segmentText,
    });

    start = line[breakIndex] === " " ? breakIndex + 1 : breakIndex;
  }

  return segments;
};

const buildChordSegment = (segment, chordPositions) => {
  if (chordPositions.length === 0) {
    return "";
  }

  const segmentStart = segment.start;
  const segmentEnd = segment.start + segment.text.length;
  const relevantChords = chordPositions.filter(
    ({ index }) => index >= segmentStart && index <= segmentEnd
  );

  if (relevantChords.length === 0) {
    return "";
  }

  let chordLine = "";
  relevantChords.forEach(({ chord, index }) => {
    const relativeIndex = Math.max(0, index - segmentStart);
    if (relativeIndex > chordLine.length) {
      chordLine = chordLine + " ".repeat(relativeIndex - chordLine.length);
    }
    chordLine = chordLine + chord;
  });

  return chordLine;
};

const buildBlocks = (tabContent) => {
  const lines = tabContent.split("\n");
  const blocks = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      blocks.push({ type: "spacer", id: `spacer-${index}` });
      continue;
    }

    if (isSectionHeader(trimmedLine)) {
      blocks.push({
        type: "section",
        id: `section-${index}`,
        text: trimmedLine.replace(/^\[|\]$/g, ""),
      });
      continue;
    }

    if (isChordLine(line) && index + 1 < lines.length && !isChordLine(lines[index + 1]) && !isSectionHeader(lines[index + 1].trim())) {
      blocks.push({
        type: "pair",
        id: `pair-${index}`,
        chords: getChordPositions(line),
        lyrics: lines[index + 1],
      });
      index += 1;
      continue;
    }

    blocks.push({
      type: isChordLine(line) ? "chords" : "lyrics",
      id: `line-${index}`,
      text: line,
    });
  }

  return blocks;
};

const ResponsiveChordSheet = ({ tabContent, fontSize }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);

    return () => observer.disconnect();
  }, []);

  const blocks = useMemo(() => buildBlocks(tabContent), [tabContent]);
  const characterWidth = useMemo(() => measureCharacterWidth(fontSize), [fontSize]);
  const maxColumns = useMemo(() => {
    if (!containerWidth || !characterWidth) {
      return 32;
    }

    return Math.max(12, Math.floor(containerWidth / characterWidth) - 1);
  }, [characterWidth, containerWidth]);

  return (
    <div
      ref={containerRef}
      className="ResponsiveChordSheet"
      style={{ fontSize: `${fontSize}px` }}
    >
      {blocks.map((block) => {
        if (block.type === "spacer") {
          return <div key={block.id} className="GuitarTabLine-spacer" />;
        }

        if (block.type === "section") {
          return (
            <div key={block.id} className="GuitarTabLine GuitarTabLine-section">
              {block.text}
            </div>
          );
        }

        if (block.type === "pair") {
          const wrappedSegments = wrapLyricLine(block.lyrics, maxColumns);

          return (
            <div key={block.id} className="ResponsiveChordSheetPair">
              {wrappedSegments.map((segment, segmentIndex) => {
                const chordLine = buildChordSegment(segment, block.chords);
                return (
                  <div key={`${block.id}-${segmentIndex}`} className="ResponsiveChordSheetSegment">
                    <div className="GuitarTabLine GuitarTabLine-chords">{chordLine || " "}</div>
                    <div className="GuitarTabLine GuitarTabLine-lyrics">{segment.text}</div>
                  </div>
                );
              })}
            </div>
          );
        }

        return (
          <div key={block.id} className={`GuitarTabLine GuitarTabLine-${block.type}`}>
            {block.text}
          </div>
        );
      })}
    </div>
  );
};

export default ResponsiveChordSheet;
